/**
 * One transcript line, styled by narrative source (architecture.md §7.3, §9).
 *
 * @remarks
 * Pure and prop-driven: the entry arrives as engine data; this component only
 * translates it into semantic, class-hooked markup. Source and priority become
 * BEM modifiers so the stylesheet — not the component — owns the visual
 * language (EURYDICE's voice, human logs, system chrome). Live-region
 * announcement is the transcript LIST's concern, not the single entry's.
 *
 * @packageDocumentation
 */

import type { CSSProperties } from 'react';

import { isFixedTranscriptSource, type RuntimeTranscriptEntry } from '@orpheus/engine-runtime';

import { revealDeclarationOf, sourceModifierOf, type SpeakerReveal } from '../revealed-text/reveal-cadence.js';
import { RevealedTextComponent } from '../revealed-text/revealed-text-component.js';

// D137: the per-channel cadence table and the entry's duration moved to the
// framework-free `reveal-cadence.ts` (the 404 page reads the same table).
// Re-exported so the transcript list is unmoved.
export { entryRevealDurationOf } from '../revealed-text/reveal-cadence.js';

/** Props for {@link TranscriptEntryComponent}. */
export interface TranscriptEntryComponentProps {
	/**
	 * The element to render as (R18-2): `p` for an ordinary line (the
	 * default), `h3` for a player echo serving as its turn group's heading —
	 * same classes, same text, document structure a screen reader can jump by.
	 */
	readonly as?: 'h3' | 'p';
	/** The structured entry to render. */
	readonly entry: RuntimeTranscriptEntry;
	/**
	 * R27: whether this entry is ARRIVING — landing on screen for the first
	 * time this session — and should perform its channel's reveal. Default
	 * false: every other surface that renders entries (dialogs, restored
	 * backlogs, re-reads of prose already seen) shows finished text, because
	 * the reveal is for arrival and a reader consulting text should never wait
	 * for it.
	 */
	readonly isArriving?: boolean;
	/**
	 * D153: a player echo the CLOCK typed — a `wait` the metronome spent on
	 * the player's behalf. Rendered as an echo (it IS a wait, and the replay
	 * reads it as one) in a dimmer treatment, so the player sees time was
	 * taken from them rather than typed by them. Presentation-local: the
	 * engine never learns who typed.
	 */
	readonly isMuted?: boolean;
	/**
	 * D102: when this entry's reveal begins, measured from the turn's start —
	 * the list sums earlier arriving lines' durations so the turn lands in
	 * order. Meaningful only with `isArriving`.
	 */
	readonly revealStartDelayMs?: number;
	/**
	 * D192: the pack's declared speakers — the cadence a speaker's line
	 * reveals at and the color it wears. Absent or empty, a speaker's line
	 * gets the metronome and the shell's default voice color.
	 */
	readonly speakers?: readonly SpeakerPresentation[];
}

/** A declared speaker as this component needs it: the reveal, the tint when the pack chose one, and (D232) the line label when a body speaks as it. */
export interface SpeakerPresentation extends SpeakerReveal {
	readonly color?: string;
	/**
	 * D232 (Calder's `actors-content.md` §6, Mike's ruling 5): the name an
	 * EMBODIED speaker's lines carry as REAL TEXT before the words — a person
	 * in the room is named; a voice in the ear is not. Present only for a
	 * speaker some actor speaks as.
	 */
	readonly lineLabel?: string;
}

/** The pack with no speakers — one frozen reference, so a defaulted prop never re-renders on identity. */
export const NO_SPEAKERS: readonly SpeakerPresentation[] = [];

/**
 * Render one structured transcript entry as a paragraph with BEM modifiers for
 * source and priority.
 *
 * @param props - {@link TranscriptEntryComponentProps}
 * @returns The rendered transcript line
 */
export const TranscriptEntryComponent = ({ as, entry, isArriving, isMuted, revealStartDelayMs, speakers = NO_SPEAKERS }: TranscriptEntryComponentProps) => {
	// D192: a speaker's line is styled as a speaker's — one class, its color
	// on the element — so the stylesheet never names a pack's character.
	const speaker = isFixedTranscriptSource(entry.source) ? undefined : speakers.find((candidate) => candidate.speakerId === entry.source);
	const sourceModifier = sourceModifierOf(entry.source);
	const reveal = revealDeclarationOf(entry.source, speakers);

	/*
	 * D84 (Calder's channel-signature ruling): the element IS the signature —
	 * markup that already means what the channel means, surviving monochrome,
	 * copy-paste, print, and whatever announcement layer is built later. A
	 * found document is a quoted document (`<blockquote>`, + the italics the
	 * stylesheet already gives it); system output is sample program output
	 * (`<samp>`, uppercased by CSS on screen). A quoted speaker's signature is
	 * the authored quotation marks (audit-enforced), `environment` is deliberately
	 * the unmarked default, and the player echo's signature is structural —
	 * the turn's heading (`as="h3"`), which outranks the channel element.
	 */
	const Element = as ?? (entry.source === 'human_log' ? 'blockquote' : 'p');
	/*
	 * D85 (Calder's media test): the quotation wraps a PARAGRAPH, not a bare
	 * inline — `<blockquote><p>` is blockquote's intended content model, and it
	 * is what gives a copied document its blank line between pages (clipboard
	 * serialization grants `<p>` two breaks and a bare blockquote one; the
	 * D84 markup silently cost documents their spacing in a paste, and the
	 * following player echo was swallowed into the last page).
	 */
	const TextElement = entry.source === 'system' ? 'samp' : entry.source === 'human_log' ? 'p' : 'span';

	return (
		<Element
			className={[
				'transcript-entry',
				`transcript-entry--source-${sourceModifier}`,
				`transcript-entry--priority-${entry.priority}`,
				// R18-2: the heading rendition keeps the entry's visual language —
				// the stylesheet resets heading typography under this modifier.
				...(Element === 'h3' ? ['transcript-entry--turn-heading'] : []),
				...(isMuted === true ? ['transcript-entry--muted'] : []),
				// D33: the provenance tag becomes a style hook too — the MAP render is
				// system-channel output that must NOT inherit the channel's uppercasing.
				...(entry.triggerContext === undefined ? [] : [`transcript-entry--trigger-${entry.triggerContext}`]),
			].join(' ')}
			data-speaker={sourceModifier === 'speaker' ? entry.source : undefined}
			data-turn={entry.turnCount}
			style={speaker?.color === undefined ? undefined : ({ '--color-voice-line': speaker.color } as CSSProperties)}
		>
			<TextElement className="transcript-entry__text">
				{entry.source === 'player' ? (
					<>
						{/* D85 (Mike's sigil ruling): the player channel's
						    character-level signature — REAL text, because a CSS
						    ::before does not copy, and the paste is the medium this
						    exists for. The D85 addendum (Calder's §3.3 measurement)
						    hides it from the ACCESSIBILITY TREE only: a
						    heading-jumping reader already holds this channel's
						    strongest signature — the echo IS the heading — so the
						    glyph there is forty redundant announcements, not
						    information. aria-hidden leaves it rendered, copied, and
						    printed; each medium carries the signature it needs and
						    none carries a redundant one. */}
						<span aria-hidden="true">{'▷ '}</span>
						{entry.text}
					</>
				) : (
					<>
						{/* D232: an actor's name before its line — REAL TEXT, in the
						    accessibility tree and the paste alike (unlike the sigil,
						    it is information: who spoke), outside the reveal because
						    the name is the transcript's substitute for looking at
						    them, not a thing they say. The colon is the export's
						    form for a labeled line, so the screen and the paste agree. */}
						{speaker?.lineLabel === undefined ? null : <span className="transcript-entry__speaker">{`${speaker.lineLabel}: `}</span>}
						{/* R27: an arriving line performs its channel's reveal; the
						    text is COMPLETE in the DOM either way (the mask constraint). */}
						<RevealedTextComponent
							cadence={isArriving === true ? reveal.cadence : 'instant'}
							startDelayMs={revealStartDelayMs}
							stepMs={reveal.stepMs}
							text={entry.text}
						/>
					</>
				)}
			</TextElement>
		</Element>
	);
};
